import React, { useState, useRef, useEffect } from 'react';
import { FaQuestionCircle, FaExternalLinkAlt, FaRedo, FaChevronDown } from 'react-icons/fa';
import { useOverlay } from '../../contexts/FocusContext';

interface HelpWidgetProps {
  onReplayTour?: () => void;
}

const HelpWidget: React.FC<HelpWidgetProps> = ({ onReplayTour }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Register as overlay when dropdown is open
  useOverlay('help-widget', isOpen);

  const handleFAQClick = () => {
    window.open('https://docs.intersectmbo.org/intersect-membership/annual-member-meetings-amm/2025-annual-members-meeting/faq', '_blank', 'noopener,noreferrer');
    setIsOpen(false);
  };

  const handleReplayTour = () => {
    if (onReplayTour) {
      onReplayTour();
    }
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  return (
    <div ref={dropdownRef} className="relative">
      {/* Toggle Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={`p-2 bg-cyan-600 bg-opacity-80 text-white rounded hover:bg-opacity-100 transition-all duration-200 shadow-lg flex items-center gap-2 ${
          isOpen ? 'bg-opacity-100' : ''
        }`}
        style={{ zIndex: 10000 }}
        title="Get Help & Support"
      >
        <FaQuestionCircle size={16} />
        <span className="text-sm font-medium">Help</span>
        <FaChevronDown 
          size={12} 
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div 
          className="absolute top-full right-0 mt-2 bg-gray-900 border border-gray-700 rounded-lg shadow-xl overflow-hidden min-w-[180px] animate-fadeIn"
          style={{ zIndex: 10001 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* FAQ & Support Option */}
          <button
            onClick={handleFAQClick}
            className="w-full px-4 py-3 flex items-center gap-3 text-white hover:bg-gray-800 transition-colors text-left"
          >
            <FaExternalLinkAlt size={14} className="text-cyan-400" />
            <span className="text-sm font-medium">FAQ & Support</span>
          </button>

          {/* Divider */}
          <div className="border-t border-gray-700" />

          {/* Replay Tutorial Option */}
          <button
            onClick={handleReplayTour}
            className="w-full px-4 py-3 flex items-center gap-3 text-white hover:bg-gray-800 transition-colors text-left"
          >
            <FaRedo size={14} className="text-purple-400" />
            <span className="text-sm font-medium">Replay Tutorial</span>
          </button>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.15s ease-out;
        }
      `}</style>
    </div>
  );
};

export default HelpWidget;
